import { marketplaceService } from '../services/marketplace';
import type {
    ShipmentContract,
    ShipmentQuote,
    ShipmentRequest,
    ShipmentRequestListStatusFilter,
    MarketplacePaginationMeta,
} from '../types/marketplace';
import {
    normalizeCollection,
    toErrorMessage,
    type MarketplaceGet,
    type MarketplaceSet,
} from './marketplaceStoreTypes';
import { synchronizeCargoInsuranceEntities } from './marketplaceCargoInsurance';

const DEFAULT_REQUESTS_PER_PAGE = 10;

function resolveEffectiveRequestStatus(request: ShipmentRequest): Exclude<ShipmentRequestListStatusFilter, 'all'> {
    if (request.contract?.status === 'completed') {
        return 'completed';
    }

    return request.status;
}

function requestMatchesFilter(request: ShipmentRequest, filter: ShipmentRequestListStatusFilter): boolean {
    if (filter === 'all') {
        return true;
    }

    return resolveEffectiveRequestStatus(request) === filter;
}

function mergeById<T extends { id: number }>(current: T[], incoming: T[]): T[] {
    const merged = new Map<number, T>();

    for (const item of current) {
        merged.set(item.id, item);
    }

    for (const item of incoming) {
        merged.set(item.id, item);
    }

    return Array.from(merged.values());
}

function mergeRequestIntoList(
    list: ShipmentRequest[],
    request: ShipmentRequest,
    filter: ShipmentRequestListStatusFilter,
    mode: 'prepend' | 'preserve'
): ShipmentRequest[] {
    const withoutCurrent = list.filter((item) => item.id !== request.id);

    if (!requestMatchesFilter(request, filter)) {
        return withoutCurrent;
    }

    if (mode === 'prepend') {
        return [request, ...withoutCurrent];
    }

    const existingIndex = list.findIndex((item) => item.id === request.id);

    if (existingIndex === -1) {
        return withoutCurrent;
    }

    return list.map((item) => (item.id === request.id ? request : item));
}

export function createRequestSlice(set: MarketplaceSet, get: MarketplaceGet) {
    return {
        myRequests: [] as ShipmentRequest[],
        myRequestsMeta: null as MarketplacePaginationMeta<ShipmentRequestListStatusFilter> | null,
        myRequestsStatusFilter: 'all' as ShipmentRequestListStatusFilter,
        feedRequests: [] as ShipmentRequest[],
        feedMeta: null as MarketplacePaginationMeta | null,
        selectedRequest: null as ShipmentRequest | null,
        requestQuotes: [] as ShipmentQuote[],
        isLoadingRequests: false,
        isSavingRequest: false,
        isLoadingFeed: false,
        isSubmittingQuote: false,
        isUpdatingCargoInsurance: false,

        fetchMyRequests: async ({
            page = 1,
            perPage = DEFAULT_REQUESTS_PER_PAGE,
            status = 'all',
            append = false,
        }: {
            page?: number;
            perPage?: number;
            status?: ShipmentRequestListStatusFilter;
            append?: boolean;
        } = {}): Promise<void> => {
            set((state) => ({
                isLoadingRequests: true,
                error: null,
                myRequestsStatusFilter: status,
                myRequests: append ? state.myRequests : [],
                myRequestsMeta: append ? state.myRequestsMeta : null,
            }));

            try {
                const response = await marketplaceService.getMyRequests({
                    page,
                    per_page: perPage,
                    status: status === 'all' ? undefined : status,
                });

                const nextPageRequests = normalizeCollection<ShipmentRequest>(response.data);

                set((state) => ({
                    myRequests: append ? mergeById(state.myRequests, nextPageRequests) : nextPageRequests,
                    myRequestsMeta: response.meta,
                    myRequestsStatusFilter: status,
                    isLoadingRequests: false,
                }));
            } catch (error) {
                set({
                    isLoadingRequests: false,
                    error: toErrorMessage(error, 'No se pudieron cargar tus solicitudes.'),
                });
            }
        },

        fetchFeedRequests: async (options: number | { page?: number; perPage?: number } = {}): Promise<void> => {
            const page = typeof options === 'number' ? options : options.page ?? 1;
            const perPage = typeof options === 'number' ? 20 : options.perPage ?? 20;

            set({ isLoadingFeed: true, error: null });

            try {
                const response = await marketplaceService.getFeedRequests({ page, per_page: perPage });

                set({
                    feedRequests: normalizeCollection<ShipmentRequest>(response.data),
                    feedMeta: response.meta,
                    isLoadingFeed: false,
                });
            } catch (error) {
                set({
                    isLoadingFeed: false,
                    error: toErrorMessage(error, 'No se pudo cargar el feed de solicitudes.'),
                });
            }
        },

        fetchRequestDetail: async (requestId: number): Promise<ShipmentRequest | null> => {
            set({ isLoadingRequests: true, error: null });

            try {
                const response = await marketplaceService.getRequestDetail(requestId);
                const request = response.data;

                set({
                    selectedRequest: request,
                    requestQuotes: normalizeCollection<ShipmentQuote>(request.quotes),
                    isLoadingRequests: false,
                });

                return request;
            } catch (error) {
                set({
                    isLoadingRequests: false,
                    error: toErrorMessage(error, 'No se pudo cargar el detalle de la solicitud.'),
                });
                return null;
            }
        },

        fetchRequestQuotes: async (requestId: number): Promise<void> => {
            set({ isSubmittingQuote: true, error: null });

            try {
                const response = await marketplaceService.getRequestQuotes(requestId);

                set({
                    requestQuotes: normalizeCollection<ShipmentQuote>(response.data),
                    isSubmittingQuote: false,
                });
            } catch (error) {
                set({
                    isSubmittingQuote: false,
                    error: toErrorMessage(error, 'No se pudieron cargar las cotizaciones.'),
                });
            }
        },

        updateCargoInsurance: async (requestId: number, choice: 'own'): Promise<ShipmentRequest> => {
            set({ isUpdatingCargoInsurance: true });

            try {
                const response = await marketplaceService.updateCargoInsurance(requestId, { choice });
                const updatedRequest = response.data;

                set((state) => ({
                    ...synchronizeCargoInsuranceEntities(state, updatedRequest),
                    isUpdatingCargoInsurance: false,
                }));

                return updatedRequest;
            } catch (error) {
                set({ isUpdatingCargoInsurance: false });
                throw error;
            }
        },

        createRequest: async (
            payload: Parameters<typeof marketplaceService.createShipmentRequest>[0],
            publishAfterCreate = false
        ): Promise<ShipmentRequest | null> => {
            set({ isSavingRequest: true, error: null });

            try {
                const createResponse = await marketplaceService.createShipmentRequest(payload);
                const createdRequest = createResponse.data;

                let finalRequest = createdRequest;

                if (publishAfterCreate) {
                    const postResponse = await marketplaceService.publishShipmentRequest(createdRequest.id);
                    finalRequest = postResponse.data;
                }

                set((state) => ({
                    selectedRequest: finalRequest,
                    myRequests: mergeRequestIntoList(
                        state.myRequests,
                        finalRequest,
                        state.myRequestsStatusFilter,
                        'prepend'
                    ),
                    isSavingRequest: false,
                }));

                return finalRequest;
            } catch (error) {
                set({
                    isSavingRequest: false,
                    error: toErrorMessage(error, 'No se pudo guardar la solicitud.'),
                });
                return null;
            }
        },

        updateRequest: async (
            requestId: number,
            payload: Parameters<typeof marketplaceService.updateShipmentRequest>[1]
        ): Promise<ShipmentRequest | null> => {
            set({ isSavingRequest: true, error: null });

            try {
                const response = await marketplaceService.updateShipmentRequest(requestId, payload);
                const updatedRequest = response.data;

                set((state) => ({
                    selectedRequest: updatedRequest,
                    myRequests: mergeRequestIntoList(
                        state.myRequests,
                        updatedRequest,
                        state.myRequestsStatusFilter,
                        'preserve'
                    ),
                    isSavingRequest: false,
                }));

                return updatedRequest;
            } catch (error) {
                set({
                    isSavingRequest: false,
                    error: toErrorMessage(error, 'No se pudo actualizar la solicitud.'),
                });
                return null;
            }
        },

        updateRequestSchedule: async (
            requestId: number,
            payload: Parameters<typeof marketplaceService.updateShipmentRequestSchedule>[1]
        ): Promise<ShipmentRequest | null> => {
            set({ isSavingRequest: true, error: null });

            try {
                const response = await marketplaceService.updateShipmentRequestSchedule(requestId, payload);
                const updatedRequest = response.data;

                set((state) => ({
                    selectedRequest: updatedRequest,
                    myRequests: mergeRequestIntoList(
                        state.myRequests,
                        updatedRequest,
                        state.myRequestsStatusFilter,
                        'preserve'
                    ),
                    isSavingRequest: false,
                }));

                return updatedRequest;
            } catch (error) {
                set({
                    isSavingRequest: false,
                    error: toErrorMessage(error, 'No se pudo actualizar la fecha y horario.'),
                });
                return null;
            }
        },

        publishRequest: async (requestId: number): Promise<ShipmentRequest | null> => {
            set({ isSavingRequest: true, error: null });

            try {
                const response = await marketplaceService.publishShipmentRequest(requestId);
                const publishedRequest = response.data;

                set((state) => ({
                    selectedRequest: publishedRequest,
                    myRequests: mergeRequestIntoList(
                        state.myRequests,
                        publishedRequest,
                        state.myRequestsStatusFilter,
                        'prepend'
                    ),
                    isSavingRequest: false,
                }));

                return publishedRequest;
            } catch (error) {
                set({
                    isSavingRequest: false,
                    error: toErrorMessage(error, 'No se pudo publicar la solicitud.'),
                });
                return null;
            }
        },

        cancelRequest: async (requestId: number): Promise<ShipmentRequest | null> => {
            set({ isSavingRequest: true, error: null });

            try {
                const response = await marketplaceService.cancelShipmentRequest(requestId);
                const cancelledRequest = response.data;

                set((state) => ({
                    selectedRequest: cancelledRequest,
                    myRequests: mergeRequestIntoList(
                        state.myRequests,
                        cancelledRequest,
                        state.myRequestsStatusFilter,
                        'preserve'
                    ),
                    isSavingRequest: false,
                }));

                return cancelledRequest;
            } catch (error) {
                set({
                    isSavingRequest: false,
                    error: toErrorMessage(error, 'No se pudo cancelar la solicitud.'),
                });
                return null;
            }
        },

        duplicateRequest: async (requestId: number): Promise<ShipmentRequest | null> => {
            set({ isSavingRequest: true, error: null });

            try {
                const response = await marketplaceService.duplicateShipmentRequest(requestId);
                const duplicatedRequest = response.data;

                set((state) => ({
                    selectedRequest: duplicatedRequest,
                    myRequests: mergeRequestIntoList(
                        state.myRequests,
                        duplicatedRequest,
                        state.myRequestsStatusFilter,
                        'prepend'
                    ),
                    isSavingRequest: false,
                }));

                return duplicatedRequest;
            } catch (error) {
                set({
                    isSavingRequest: false,
                    error: toErrorMessage(error, 'No se pudo duplicar la solicitud.'),
                });
                return null;
            }
        },

        createQuote: async (
            requestId: number,
            payload: Parameters<typeof marketplaceService.createQuote>[1]
        ): Promise<ShipmentQuote | null> => {
            set({ isSubmittingQuote: true, error: null });

            try {
                const response = await marketplaceService.createQuote(requestId, payload);
                const quote = response.data;

                set((state) => ({
                    requestQuotes: [
                        quote,
                        ...state.requestQuotes.filter((item) => item.id !== quote.id),
                    ],
                    isSubmittingQuote: false,
                }));

                return quote;
            } catch (error) {
                set({
                    isSubmittingQuote: false,
                    error: toErrorMessage(error, 'No se pudo enviar la cotización.'),
                });
                return null;
            }
        },

        selectQuote: async (quoteId: number): Promise<ShipmentContract | null> => {
            set({ isSubmittingQuote: true, error: null });

            try {
                const response = await marketplaceService.selectQuote(quoteId);
                const contract = response.data;

                set({
                    selectedContract: contract,
                    isSubmittingQuote: false,
                });

                const selectedRequest = get().selectedRequest;
                if (selectedRequest && selectedRequest.id === contract.shipment_request_id) {
                    set((state) => ({
                        selectedRequest: {
                            ...selectedRequest,
                            contract: {
                                id: contract.id,
                                status: contract.status,
                                tracking_phase: contract.tracking_phase ?? null,
                                tracking_started_at: contract.tracking_started_at ?? null,
                                tracking_completed_at: contract.tracking_completed_at ?? null,
                            },
                        },
                        myRequests: mergeRequestIntoList(
                            state.myRequests,
                            {
                                ...selectedRequest,
                                contract: {
                                    id: contract.id,
                                    status: contract.status,
                                    tracking_phase: contract.tracking_phase ?? null,
                                    tracking_started_at: contract.tracking_started_at ?? null,
                                    tracking_completed_at: contract.tracking_completed_at ?? null,
                                },
                            },
                            state.myRequestsStatusFilter,
                            'preserve'
                        ),
                    }));
                }

                return contract;
            } catch (error) {
                set({
                    isSubmittingQuote: false,
                    error: toErrorMessage(error, 'No se pudo seleccionar la cotización.'),
                });
                return null;
            }
        },
    };
}
